import { Collapse, Icon, IconButton, Paper, Typography } from '@shared/ui';
import React, { useState } from 'react';
import { registerWidgetRenderer } from '../registry';
import type { StatusEntity, TimelineWidgetProps } from '../types';

export function StatusWidget({ entity }: TimelineWidgetProps<StatusEntity>) {
  const { text, type = 'info', ...additionalFields } = entity.props;
  const [expanded, setExpanded] = useState(false);

  // Check if there are any additional fields beyond text and type
  const hasAdditionalFields = Object.keys(additionalFields).length > 0;

  const configs: Record<string, { icon: string; color: string; bg: string }> = {
    info: {
      icon: 'info',
      color: 'text.info',
      bg: 'bg-blue-50',
    },
    success: {
      icon: 'check_circle',
      color: 'text.success',
      bg: 'bg-green-50',
    },
    warning: {
      icon: 'warning',
      color: 'text.warning',
      bg: 'bg-yellow-50',
    },
    error: {
      icon: 'error',
      color: 'text.error',
      bg: 'bg-red-50',
    },
  };

  const config = configs[type] || configs.info;

  const renderFieldValue = (value: any): React.ReactNode => {
    if (value === null) return <span className="text-gray-400">null</span>;
    if (value === undefined) return <span className="text-gray-400">undefined</span>;
    if (typeof value === 'boolean') return <span className="text-purple-600">{String(value)}</span>;
    if (typeof value === 'number') return <span className="text-blue-600">{value}</span>;
    if (typeof value === 'string') return <span className="text-green-700">"{value}"</span>;
    if (Array.isArray(value)) {
      return (
        <div className="overflow-x-auto max-w-full mt-1">
          <pre className="text-xs bg-white/50 p-1 rounded overflow-x-auto max-h-32 whitespace-pre">
            {JSON.stringify(value, null, 2)}
          </pre>
        </div>
      );
    }
    if (typeof value === 'object') {
      return (
        <div className="overflow-x-auto max-w-full mt-1">
          <pre className="text-xs bg-white/50 p-1 rounded overflow-x-auto max-h-32 whitespace-pre">
            {JSON.stringify(value, null, 2)}
          </pre>
        </div>
      );
    }
    return String(value);
  };

  return (
    <Paper variant="outlined" className={`p-2 w-full min-w-0 ${config.bg} border-l-4`}>
      <div className="flex items-center gap-2 min-w-0">
        <Icon sx={{ fontSize: 18, color: config.color, flexShrink: 0 }}>{config.icon}</Icon>
        <Typography variant="caption" color="text.secondary" className="flex-1 min-w-0 break-words">
          {text}
        </Typography>
        {hasAdditionalFields && (
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ padding: '2px', flexShrink: 0 }}
          >
            <Icon sx={{ fontSize: 16 }}>{expanded ? 'expand_less' : 'expand_more'}</Icon>
          </IconButton>
        )}
        <Typography variant="caption" color="text.secondary" className="flex-shrink-0">
          {new Date(entity.timestamp).toLocaleTimeString()}
        </Typography>
      </div>

      {hasAdditionalFields && (
        <Collapse in={expanded}>
          <div className="mt-2 pt-2 border-t border-gray-200 min-w-0">
            <Typography variant="caption" className="font-semibold text-gray-600 mb-1 block">
              Additional Fields:
            </Typography>
            <div className="space-y-1">
              {Object.entries(additionalFields).map(([key, value]) => (
                <div key={key} className="text-xs min-w-0">
                  <span className="font-mono font-semibold text-gray-700">{key}:</span>{' '}
                  {renderFieldValue(value)}
                </div>
              ))}
            </div>
          </div>
        </Collapse>
      )}
    </Paper>
  );
}

// Register renderer + visibility
registerWidgetRenderer(
  'status',
  ({ entity }) => <StatusWidget key={(entity as any).id} entity={entity as any} />,
  { visibility: { normal: false, debug: true } }
);
